/**
 * Kingspan tank core — the single source of truth for the tank-domain logic
 * that the PDP configurator (kingspan-urban-tank-configurator.js) and the
 * chat tank builder (kingspan-chat-tank-builder.js) previously duplicated
 * and let drift. Pure functions over plain arguments: no DOM, no state
 * (onPromoChange only arms a timer for its caller).
 *
 * Also the one place for the painted-colour tier/limit rules and the builder
 * offer rule (TANK_PROMO, PUMPS): two independent offers, "tank"
 * (Slimline/Modline tank price) and "pump" (any one of the six PUMPS with a
 * builder tank, each pump with its own list-price floor). The KPO app's
 * checkout discount function enforces the same rule (one automatic discount
 * per offer); .catalog-source/test/promo-cases.json is shared by both test
 * suites, so change the rule, the cases and the function together.
 *
 * What is on, when and where comes from ONE place: the shop metafield
 * kx_promo.offers, which the KPO operator script derives from those two
 * discounts. layout/theme.liquid prints it as window.KingspanTankOffers;
 * promoPolicy() validates it and fails closed (no metafield, a malformed one,
 * a disabled, not yet started or ended offer: no offer shown).
 * .catalog-source/test/promo-snapshot-cases.json (shared with KPO) pins it.
 *
 * Load order: this file must execute before any consumer (the PDP snippet and
 * the chat bootstrap emit it first; the preset cards' kx-preset-cards.js loads it).
 * window.KingspanTankOffers is emitted inline in <head> by layout/theme.liquid.
 *
 * Deliberately NOT shared: each surface's derived usage stats (flushes/
 * showers/loads divide capacity by different constants by design) and the
 * cart-property bags (the two flows carry different line metadata).
 */
(function () {
  'use strict';

  /** values: { height, width, length, diameter, sWidth } (strings or numbers). */
  function skuKey(typeKey, values) {
    var v = values || {};
    if (typeKey === 'slimline' || typeKey === 'modline') {
      if (!v.height || !v.width || !v.length) return null;
      return typeKey + '|' + v.height + '|' + v.width + '|' + v.length;
    }
    if (typeKey === 'round') {
      if (!v.height || !v.diameter) return null;
      return 'round|' + v.height + '|' + v.diameter;
    }
    if (typeKey === 'square') {
      if (!v.height || !v.sWidth) return null;
      return 'square|' + v.height + '|' + v.sWidth;
    }
    return null;
  }

  function resolveTank(map, typeKey, values) {
    var tanks = map && map.tanks;
    if (!tanks) return null;
    var key = skuKey(typeKey, values);
    if (!key) return null;
    return tanks[key] || null;
  }

  /** True when the map cannot price by size and ATC must use the BYO base variant. */
  function useBaseVariantCart(map) {
    if (!map) return true;
    if (map.useBaseVariant === true) return true;
    if (String(map.mode || '').indexOf('base_variant') !== -1) return true;
    var tanks = map.tanks;
    if (!tanks || typeof tanks !== 'object' || !Object.keys(tanks).length) return true;
    return false;
  }

  function num(v) {
    var n = Number(String(v == null ? '' : v).replace(/[^\d.]/g, ''));
    return isFinite(n) ? n : 0;
  }

  /** Litres, from the Expivi catalogue formulas. Identical across surfaces. */
  function computeCapacity(typeKey, values) {
    var length = num(values.length);
    var width = num(values.width);
    var height = num(values.height);
    var diameter = num(values.diameter);
    var sWidth = num(values.sWidth);
    var modCoefficient = width >= 1100 ? 0.1373 : 0.0773;
    var capacity = 0;
    switch (typeKey) {
      case 'slimline':
        capacity = Math.round(
          (height * (Math.PI * Math.pow(0.5 * width, 2) + width * (length - width))) / 1000000
        );
        break;
      case 'modline':
        capacity = Math.round((height * width * length) / 1000000 - height * modCoefficient);
        break;
      case 'round':
        capacity = Math.round((diameter / 2000) * (diameter / 2000) * Math.PI * (height / 1000) * 1000);
        break;
      case 'square':
        capacity =
          Math.floor(
            (((sWidth * sWidth - (600 * 600 - Math.PI * 300 * 300)) * height) / 1000000) / 10
          ) * 10;
        break;
      default:
        break;
    }
    if (!isFinite(capacity) || capacity < 0) capacity = 0;
    return capacity;
  }

  /** Kilograms (5kg steps), Aquaplate steel shell. */
  function computeWeight(typeKey, values) {
    var length = num(values.length);
    var width = num(values.width);
    var height = num(values.height);
    var diameter = num(values.diameter);
    var sWidth = num(values.sWidth);
    var modRadius2 = 312;
    switch (typeKey) {
      case 'slimline':
        return (
          Math.ceil(
            (((((Math.PI * width * height) +
              (length - width) * height * 2 +
              (2 * Math.PI * Math.pow(width, 2)) / 4 +
              2 * (length - width) * width) /
              1000000) *
              6.21) /
              5)
          ) * 5
        );
      case 'modline':
        return (
          Math.round(
            ((2 * height * (width - 2 * modRadius2) +
              2 * height * (length - 2 * modRadius2) +
              2 *
                (length * width -
                  (modRadius2 * modRadius2 - Math.PI * modRadius2 * modRadius2)) +
              height * 2 * modRadius2 * Math.PI) /
              1000000) *
              6.21 /
              5
          ) * 5
        );
      case 'round':
        return (
          Math.ceil(
            ((((Math.PI * diameter * height) + (2 * Math.PI * (diameter * diameter)) / 4) / 1000000) *
              6.21) /
              5
          ) * 5
        );
      case 'square':
        return (
          Math.ceil(
            ((((((4 * sWidth) - 8 * 300 + Math.PI * 600) * height) + 2 * sWidth * sWidth) /
              1000000) *
              6.21) /
              5
          ) * 5
        );
      default:
        return 0;
    }
  }

  function sizeList(list) {
    return Array.isArray(list) ? list.map(String) : [];
  }

  /**
   * The sizes a builder offers for a shape (fb-40240: the chat tells the agent what the
   * panel lists), from tank-size-rules.json with the cascade both builders use:
   *   Slimline / Modline: { widths, lengths, heights }: lengths at values.width
   *     (lengthsByWidth, then lengths); heights at width x length (heightsByWidthLength,
   *     then heightsByWidth, then heights).
   *   Round: { diameters, heights }: heights at values.diameter (heightsByDiameter, then heights).
   *   Square: { widths, heights }: widths are the equal sides; heights at values.sWidth
   *     (heightsByWidth, then heights).
   * A list the panel has not reached is empty, as the panel shows it: no lengths before a
   * width, no heights before the footprint (width and length, diameter, side) is chosen.
   * litres: { height: litres } for each listed height (computeCapacity).
   * litresAt: the litres of each other width, length or diameter with the rest of the build
   *   kept ({ widths: {w: L}, lengths: {l: L}, diameters: {d: L} }), once a height is chosen
   *   and only where the panel offers that height there (so a size question about another
   *   footprint has its real figure, not one worked out from this footprint's heights).
   * The rules have no region: only which shapes a state can have varies (catalogue
   * regionsByType). null for an unknown shape or no rules for it.
   */
  function sizeOptions(sizeRules, typeKey, values) {
    var m = sizeRules && sizeRules[typeKey];
    if (!m || typeof m !== 'object') return null;
    var v = values || {};
    var h = String(v.height || '');
    var out;
    var at = { widths: {}, lengths: {}, diameters: {} };
    function cap(patch) {
      var size = {};
      for (var k in v) if (Object.prototype.hasOwnProperty.call(v, k)) size[k] = v[k];
      for (var p in patch) if (Object.prototype.hasOwnProperty.call(patch, p)) size[p] = patch[p];
      return computeCapacity(typeKey, size);
    }
    if (typeKey === 'round') {
      var d = String(v.diameter || '');
      var roundHeights = function (dv) {
        return sizeList((m.heightsByDiameter || {})[dv] || m.heights);
      };
      out = { diameters: sizeList(m.diameters), heights: d ? roundHeights(d) : [] };
      if (h) {
        out.diameters.forEach(function (dv) {
          if (roundHeights(dv).indexOf(h) >= 0) at.diameters[dv] = cap({ diameter: dv, height: h });
        });
      }
    } else if (typeKey === 'square') {
      var side = String(v.sWidth || '');
      var squareHeights = function (sv) {
        return sizeList((m.heightsByWidth || {})[sv] || m.heights);
      };
      out = { widths: sizeList(m.widths), heights: side ? squareHeights(side) : [] };
      if (h) {
        out.widths.forEach(function (sv) {
          if (squareHeights(sv).indexOf(h) >= 0) at.widths[sv] = cap({ sWidth: sv, height: h });
        });
      }
    } else if (typeKey === 'slimline' || typeKey === 'modline') {
      var w = String(v.width || '');
      var l = String(v.length || '');
      var lengthsAt = function (wv) {
        return sizeList((m.lengthsByWidth || {})[wv] || m.lengths);
      };
      var heightsAt = function (wv, lv) {
        return sizeList((m.heightsByWidthLength || {})[wv + 'x' + lv] || (m.heightsByWidth || {})[wv] || m.heights);
      };
      out = { widths: sizeList(m.widths), lengths: w ? lengthsAt(w) : [], heights: w && l ? heightsAt(w, l) : [] };
      if (h && w) {
        out.lengths.forEach(function (lv) {
          if (heightsAt(w, lv).indexOf(h) >= 0) at.lengths[lv] = cap({ length: lv, height: h });
        });
      }
      if (h && l) {
        out.widths.forEach(function (wv) {
          if (lengthsAt(wv).indexOf(l) >= 0 && heightsAt(wv, l).indexOf(h) >= 0) at.widths[wv] = cap({ width: wv, height: h });
        });
      }
    } else {
      return null;
    }
    out.litres = {};
    for (var i = 0; i < out.heights.length; i++) out.litres[out.heights[i]] = cap({ height: out.heights[i] });
    out.litresAt = at;
    return out;
  }

  /**
   * Sizes near a capacity (owner feedback 2026-10-14: "how would I know how to choose the one
   * closest to 2000 litres"). Every size a builder offers for the shape (the sizeOptions cascade
   * over tank-size-rules.json), with litres from computeCapacity, ranked against `litres`.
   * opts:
   *   priceOf(values)  the size's price, or null when it can't be priced: unpriced sizes are left
   *                    out, so every row can go in the cart (with no priced size at all, e.g. no
   *                    sku map, every size counts, price null).
   *   paintedOnly      only sizes Kingspan paints (paintable): the build has a painted colour.
   *   minHeight        only heights from this many mm: the build's pump has a height rule.
   *   rankOf(values, price)  what a priced size in the band is ranked on in place of its price,
   *                    e.g. the Est. total its row shows (a painted colour's surcharge steps up
   *                    by capacity, so the cheapest tank need not be the cheapest build); called
   *                    only for sizes in the band; a row keeps its price. Default: the price.
   * Within 5% of the target (the band; "the cheapest" below is on rankOf when given):
   *   best       the pick: the cheapest from the target up to 5% above it (a shopper who names
   *              litres usually means at least that), ties to the closest; with none at or
   *              above, the cheapest in the band (pickBasis 'above' / 'near');
   *   closest    the smallest litre difference, then the cheapest;
   *   narrowest  the narrowest width (diameter, side), then the cheapest, then the closest;
   *   lowest     the lowest height, then the cheapest, then the closest.
   * A size that wins twice is one row with both labels; rows in that order. Ties keep the size
   * rules' order. Nothing in the band: no pick, status 'above' (more than the largest: that
   * size), 'below' (less than the smallest: that size) or 'gap' (the nearest either side).
   * Returns { typeKey, target, status: 'ok'|'above'|'below'|'gap'|'none', pick, pickBasis, rows,
   * largest, smallest, within, sizes }; a row is { values, litres, price, labels }. null for an
   * unknown shape, no rules for it, or no target. Pure, like sizeOptions; callers memoise.
   */
  function sizesNearCapacity(sizeRules, typeKey, litres, opts) {
    var m = sizeRules && sizeRules[typeKey];
    var target = num(litres);
    if (!m || typeof m !== 'object' || !(target > 0)) return null;
    var o = opts || {};
    var minHeight = num(o.minHeight);
    var all = [];
    function add(values) {
      if (minHeight && num(values.height) < minHeight) return;
      if (o.paintedOnly && !paintable(typeKey, values)) return;
      var l = computeCapacity(typeKey, values);
      if (!(l > 0)) return;
      var p = typeof o.priceOf === 'function' ? o.priceOf(values) : null;
      var n = p == null || p === '' ? NaN : Number(p);
      all.push({ values: values, litres: l, price: isFinite(n) ? n : null });
    }
    if (typeKey === 'round') {
      sizeList(m.diameters).forEach(function (d) {
        sizeList((m.heightsByDiameter || {})[d] || m.heights).forEach(function (h) {
          add({ diameter: d, height: h });
        });
      });
    } else if (typeKey === 'square') {
      sizeList(m.widths).forEach(function (s) {
        sizeList((m.heightsByWidth || {})[s] || m.heights).forEach(function (h) {
          add({ sWidth: s, height: h });
        });
      });
    } else if (typeKey === 'slimline' || typeKey === 'modline') {
      sizeList(m.widths).forEach(function (w) {
        sizeList((m.lengthsByWidth || {})[w] || m.lengths).forEach(function (l) {
          sizeList((m.heightsByWidthLength || {})[w + 'x' + l] || (m.heightsByWidth || {})[w] || m.heights).forEach(function (h) {
            add({ width: w, length: l, height: h });
          });
        });
      });
    } else {
      return null;
    }
    var priced = all.filter(function (r) {
      return r.price != null;
    });
    var pool = priced.length ? priced : all;
    var widthKey = typeKey === 'round' ? 'diameter' : typeKey === 'square' ? 'sWidth' : 'width';
    function price(r) {
      if (r.rank != null) return r.rank;
      return r.price == null ? Infinity : r.price;
    }
    function diff(r) {
      return Math.abs(r.litres - target);
    }
    // Comparators: < 0 when a ranks first; two unpriced sizes (Infinity - Infinity) tie.
    function byPrice(a, b) {
      return price(a) - price(b) || 0;
    }
    function byClose(a, b) {
      return diff(a) - diff(b) || byPrice(a, b);
    }
    function byPriceThenClose(a, b) {
      return byPrice(a, b) || diff(a) - diff(b);
    }
    function byNarrow(a, b) {
      return num(a.values[widthKey]) - num(b.values[widthKey]) || byPriceThenClose(a, b);
    }
    function byLow(a, b) {
      return num(a.values.height) - num(b.values.height) || byPriceThenClose(a, b);
    }
    function byMost(a, b) {
      return b.litres - a.litres || byPrice(a, b);
    }
    function byLeast(a, b) {
      return a.litres - b.litres || byPrice(a, b);
    }
    /** The size that ranks first (a tie keeps the earlier size, so the result is stable). */
    function first(list, cmp) {
      var best = null;
      for (var i = 0; i < list.length; i++) if (!best || cmp(list[i], best) < 0) best = list[i];
      return best;
    }
    function row(r) {
      if (!r) return null;
      var values = {};
      for (var k in r.values) if (hasOwn(r.values, k)) values[k] = r.values[k];
      return { values: values, litres: r.litres, price: r.price, labels: [] };
    }
    var out = {
      typeKey: typeKey,
      target: target,
      status: 'none',
      pick: null,
      pickBasis: '',
      rows: [],
      largest: null,
      smallest: null,
      within: 0,
      sizes: pool.length,
    };
    if (!pool.length) return out;
    var made = [];
    function label(r, name) {
      if (!r) return;
      for (var i = 0; i < made.length; i++) {
        if (made[i].src === r) {
          made[i].row.labels.push(name);
          return;
        }
      }
      var next = row(r);
      next.labels.push(name);
      made.push({ src: r, row: next });
    }
    var largest = first(pool, byMost);
    var smallest = first(pool, byLeast);
    out.largest = row(largest);
    out.smallest = row(smallest);
    var band = pool.filter(function (r) {
      return diff(r) <= target * 0.05;
    });
    out.within = band.length;
    if (typeof o.rankOf === 'function') {
      band.forEach(function (r) {
        if (r.price == null) return;
        var x = Number(o.rankOf(r.values, r.price));
        if (isFinite(x)) r.rank = x;
      });
    }
    if (!band.length) {
      if (target > largest.litres) {
        out.status = 'above';
        label(largest, 'largest');
      } else if (target < smallest.litres) {
        out.status = 'below';
        label(smallest, 'smallest');
      } else {
        out.status = 'gap';
        label(first(pool.filter(function (r) { return r.litres < target; }), byMost), 'below');
        label(first(pool.filter(function (r) { return r.litres > target; }), byLeast), 'above');
      }
    } else {
      var up = band.filter(function (r) {
        return r.litres >= target;
      });
      label(first(up.length ? up : band, byPriceThenClose), 'best');
      label(first(band, byClose), 'closest');
      label(first(band, byNarrow), 'narrowest');
      label(first(band, byLow), 'lowest');
      out.status = 'ok';
      out.pickBasis = up.length ? 'above' : 'near';
    }
    out.rows = made.map(function (x) {
      return x.row;
    });
    if (out.status === 'ok') out.pick = out.rows[0];
    return out;
  }

  /** Regional Aquaplate colour chart for a tank type. */
  function colourPalette(constraints, typeKey, region) {
    var c = constraints || {};
    var byType =
      (typeKey === 'square' && c.squareColoursByRegion) ||
      (typeKey === 'round' && c.roundColoursByRegion) ||
      (typeKey === 'modline' && c.modlineColoursByRegion) ||
      (typeKey === 'slimline' && c.slimlineColoursByRegion) ||
      c.urbanColoursByRegion ||
      c.slimlineColoursByRegion ||
      {};
    return byType[region] || byType['NSW / ACT'] || { all: [], base: [], painted: [] };
  }

  function findColour(palette, name) {
    return ((palette && palette.all) || []).find(function (c) { return c.name === name; }) || null;
  }

  function addonEntry(map, field) {
    return (map && map.addons && map.addons[field]) || null;
  }

  function addonPrice(map, field, fallback) {
    var entry = addonEntry(map, field);
    var n = entry ? Number(entry.price) : NaN;
    return isFinite(n) ? n : fallback;
  }

  /**
   * Deprecated: always tier 1, whatever the size. Use paintedTier(). Kept
   * exported only until every caller has moved.
   */
  function paintedTier1(palette, colourName) {
    var c = findColour(palette, colourName);
    return c && c.kind === 'painted' && Number(c.price) > 0
      ? { key: 'paintedColour', colour: c.name }
      : null;
  }

  /*
   * Painted colours. Urban Retail Price List V13 p16: one national surcharge
   * table banded by tank capacity — up to 10,999 L $240 (KUT-PAINT-1), up to
   * 20,999 L $655 (KUT-PAINT-2), above that $985 (KUT-PAINT-3); thresholds as
   * the old Expivi rules ("Total capacity > 10999" / "> 20999"). Which colours
   * count as painted in each state stays in the catalogue palettes.
   * Kingspan (2026-09-24): tanks over 2470 mm high or over 3000 mm in diameter
   * are not painted (same limits as connect-chat-api/build-tank-sizes.js).
   */
  var PAINT_TIERS = freeze([
    freeze({ maxLitres: 10999, key: 'paintedColour' }),
    freeze({ maxLitres: 20999, key: 'paintedColour2' }),
    freeze({ maxLitres: Infinity, key: 'paintedColour3' }),
  ]);
  var PAINT_LIMITS = freeze({ maxHeightMm: 2470, maxDiameterMm: 3000 });

  /** False when Kingspan won't paint this size (base colours only). */
  function paintable(typeKey, values) {
    var v = values || {};
    if (num(v.height) > PAINT_LIMITS.maxHeightMm) return false;
    return !(typeKey === 'round' && num(v.diameter) > PAINT_LIMITS.maxDiameterMm);
  }

  /** sku-map addon field of the painted-colour surcharge for a capacity in litres. */
  function paintTierKey(capacityL) {
    var litres = num(capacityL);
    for (var i = 0; i < PAINT_TIERS.length; i++) {
      if (litres <= PAINT_TIERS[i].maxLitres) return PAINT_TIERS[i].key;
    }
    return PAINT_TIERS[PAINT_TIERS.length - 1].key;
  }

  /**
   * The capacity-tiered surcharge for the chosen colour: { key, colour }, or
   * null for a base colour, an unknown colour or a size Kingspan won't paint.
   * `constraints` is unused (the rules live in code) and kept for callers.
   */
  function paintedTier(constraints, palette, colourName, typeKey, values) {
    var c = findColour(palette, colourName);
    if (!c || c.kind !== 'painted' || !(Number(c.price) > 0)) return null;
    if (!paintable(typeKey, values)) return null;
    return { key: paintTierKey(computeCapacity(typeKey, values || {})), colour: c.name };
  }

  /**
   * What a painted colour would add at this size (for swatch labels and the
   * section header): 0 when the size can't be painted or the tier isn't mapped.
   */
  function paintedColourPrice(map, typeKey, values) {
    if (!paintable(typeKey, values)) return 0;
    return paintSurcharge(map, [{ key: paintTierKey(computeCapacity(typeKey, values || {})) }]);
  }

  /**
   * Displayed paint surcharge for the given tiers — only what the cart can
   * actually charge as sku-map addon lines, so shown totals never exceed
   * checkout. Unmapped tiers contribute (and charge) 0.
   */
  function paintSurcharge(map, tiers) {
    var total = 0;
    (tiers || []).forEach(function (tier) {
      var entry = addonEntry(map, tier.key);
      var n = entry ? Number(entry.price) : NaN;
      if (entry && entry.variantLegacyId && isFinite(n)) total += n;
    });
    return total;
  }

  /*
   * Builder offers — the ONE rule the PDP builder, chat builder and featured
   * cards price from. Two independent offers, for builds delivered to
   * NSW/ACT, VIC or QLD (never SA; a missing or unknown region is not
   * eligible either):
   *  - "tank": 10% off the tank for Slimline and Modline (field 'tank');
   *  - "pump": 10% off ONE pump (a PUMPS field) bought with any builder tank
   *    (Slimline, Modline, Round or Square). At most one discounted pump per
   *    tank: the first in PUMPS order that qualifies (checkout ranks a build's
   *    pump lines the same way, whatever their cart order).
   * Each pump has its own floor, its LIST price (PUMPS[i].minUnitPrice):
   * below it nothing comes off, here and at checkout, so a price that is
   * already reduced is never discounted a second time. Every other add-on
   * (paint, fire fit, fittings) is full price.
   * Which offer is on, from when to when, for which regions, tank SKU
   * prefixes and pump variants comes from the shop metafield kx_promo.offers
   * (see promoPolicy); it can narrow this rule, never widen it. The KPO
   * checkout discount function enforces the same rule (one automatic discount
   * per offer: tanks by SKU prefix, pumps by variant id and per-SKU floor,
   * the delivery province).
   */
  var BUILDER_TYPES = ['slimline', 'modline', 'round', 'square'];

  /**
   * The pumps the pump offer covers, in display AND cart-line order (old
   * links with two pumps keep the first; with two pumps on one tank the
   * first that qualifies is the discounted one). Per pump: the build field; the
   * add-on variant's sku (the KPO rule keys its floors by it); item (what an
   * offer line names: "10% off with your tank — Claytech C3X pump"); title
   * (the add-on's own name = its sku-map title, for a cart line or row when
   * the map has none); minUnitPrice (its LIST price in AUD = the offer's
   * floor). The KPO function's RULE.pump.minUnitPriceBySku must be exactly
   * { sku: minUnitPrice } of this table (parity tests in both repos).
   */
  var PUMPS = freeze([
    freeze({ field: 'mk3Pump', sku: 'KUT-PUMP-MKIV', item: 'Evolution MKIV pump', title: 'Submersible Pump — Evolution MKIV', minUnitPrice: 1250 }),
    freeze({ field: 'integratedGardenPump', sku: 'KUT-PUMP-GARDEN', item: 'Integrated Garden Pump System', title: 'Integrated Garden Pump System', minUnitPrice: 1087 }),
    freeze({ field: 'claytechC3X', sku: 'KUT-PUMP-C3X', item: 'Claytech C3X pump', title: 'Claytech C3X', minUnitPrice: 660 }),
    freeze({ field: 'claytechC3X20mmAcqua', sku: 'KUT-PUMP-C3X-MS', item: 'Claytech C3X pump with mains switching', title: 'Claytech C3X + Mains Switching', minUnitPrice: 890 }),
    freeze({ field: 'claytechC5X', sku: 'KUT-PUMP-C5X', item: 'Claytech C5X pump', title: 'Claytech C5X', minUnitPrice: 770 }),
    freeze({ field: 'claytechC5X20mmAcqua', sku: 'KUT-PUMP-C5X-MS', item: 'Claytech C5X pump with mains switching', title: 'Claytech C5X + Mains Switching', minUnitPrice: 1000 }),
  ]);
  var PUMP_FIELDS = freeze(PUMPS.map(function (p) { return p.field; }));
  var PUMP_PCT = 10;

  var TANK_PROMO = freeze({
    regions: freeze(['NSW / ACT', 'VIC', 'QLD']),
    never: freeze(['SA']),
    provinces: freeze(['NSW', 'ACT', 'VIC', 'QLD']),
    /** The rule's widest wording; an offer's own wording comes from its regions (promoTerms). */
    regionsText: 'NSW/ACT, VIC & QLD',
    tank: freeze({ pct: 10, types: freeze(['slimline', 'modline']), skuPrefixes: freeze(['SL-', 'ML-']) }),
    /** fields: the PUMPS fields; perTank: discounted pumps per tank (the KPO function's cap). */
    pump: freeze({ pct: PUMP_PCT, withTypes: freeze(BUILDER_TYPES.slice()), perTank: 1, fields: PUMP_FIELDS }),
    /** { [pump field]: pump.pct }: every add-on field the offers can discount. */
    addons: freeze(PUMP_FIELDS.reduce(function (o, f) { o[f] = PUMP_PCT; return o; }, {})),
  });

  /**
   * The two offers in display order: key (policy[key], the snapshot's key),
   * the build fields each one discounts, and the label shown when the
   * snapshot gives none (= the KPO function's DEFAULT_MESSAGES).
   */
  var PROMO_OFFERS = freeze([
    freeze({ key: 'tank', fields: freeze(['tank']), defaultLabel: '10% off' }),
    freeze({ key: 'pump', fields: PUMP_FIELDS, defaultLabel: '10% off with your tank' }),
  ]);

  /** The version of kx_promo.offers this file reads; any other version shows no offer. */
  var SNAPSHOT_VERSION = 1;
  var VARIANT_GID = /^gid:\/\/shopify\/ProductVariant\/\d+$/;

  function freeze(o) {
    return Object.freeze ? Object.freeze(o) : o;
  }

  /** Strict list membership: false for anything that is not an array. */
  function has(list, value) {
    return Array.isArray(list) && list.indexOf(value) !== -1;
  }

  function hasOwn(o, k) {
    return !!o && Object.prototype.hasOwnProperty.call(o, k);
  }

  function isObject(o) {
    return !!o && typeof o === 'object' && !Array.isArray(o);
  }

  /** The PUMPS entry for a build field, or null (for any value, 'toString' included). */
  function pumpInfo(field) {
    for (var i = 0; i < PUMPS.length; i++) if (PUMPS[i].field === field) return PUMPS[i];
    return null;
  }

  /** Exact catalogue region value ("NSW / ACT", "VIC", "QLD"); never SA. */
  function promoRegionEligible(region) {
    var r = String(region == null ? '' : region);
    return has(TANK_PROMO.regions, r) && !has(TANK_PROMO.never, r);
  }

  /** Delivery address side of the rule (what checkout checks): AU and NSW, ACT, VIC or QLD. */
  function promoProvinceEligible(provinceCode, countryCode) {
    if (countryCode != null && String(countryCode).toUpperCase() !== 'AU') return false;
    var p = String(provinceCode == null ? '' : provinceCode).toUpperCase();
    return has(TANK_PROMO.provinces, p) && !has(TANK_PROMO.never, p);
  }

  /*
   * NSW daylight saving (rule since 2008): AEDT (UTC+11) from 02:00 on the
   * first Sunday of October to 03:00 on the first Sunday of April, otherwise
   * AEST (UTC+10). Changeovers are never at midnight, so the offset at 00:00
   * depends on the date alone. Tested against the IANA zone in
   * .catalog-source/test/kingspan-tank-core.test.js.
   */
  function firstSunday(year, month) {
    return 1 + ((7 - new Date(Date.UTC(year, month - 1, 1)).getUTCDay()) % 7);
  }

  function sydneyOffsetAtMidnight(year, month, day) {
    if (month > 4 && month < 10) return 10;
    if (month < 4 || month > 10) return 11;
    if (month === 4) return day <= firstSunday(year, 4) ? 11 : 10;
    return day > firstSunday(year, 10) ? 11 : 10;
  }

  /**
   * "YYYY-MM-DD" = an offer's last day in Sydney. Returns the epoch ms the
   * offer stops (00:00 Sydney time the next day), or NaN for a blank or
   * invalid date. (The KPO script's endsAtFor gives the same instant.)
   */
  function promoEndMs(endsAt) {
    var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(endsAt == null ? '' : endsAt).trim());
    if (!m) return NaN;
    var y = Number(m[1]);
    var mo = Number(m[2]);
    var d = Number(m[3]);
    var day = new Date(Date.UTC(y, mo - 1, d));
    if (y < 1970 || day.getUTCFullYear() !== y || day.getUTCMonth() !== mo - 1 || day.getUTCDate() !== d) {
      return NaN;
    }
    var next = new Date(Date.UTC(y, mo - 1, d + 1));
    var ny = next.getUTCFullYear();
    var nm = next.getUTCMonth() + 1;
    var nd = next.getUTCDate();
    return next.getTime() - sydneyOffsetAtMidnight(ny, nm, nd) * 3600000;
  }

  var ISO_INSTANT = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2})(?:\.(\d{1,3}))?)?(Z|([+-])(\d{2}):(\d{2}))$/;

  /**
   * An exact instant, ISO 8601 with a zone ("2026-11-01T13:00:00.000Z",
   * "2026-11-02T00:00:00+11:00"), as epoch ms; NaN for anything else,
   * including impossible dates (Date.parse would roll 30 Feb into March).
   */
  function instantMs(value) {
    if (typeof value !== 'string') return NaN;
    var m = ISO_INSTANT.exec(value.trim());
    if (!m) return NaN;
    var y = Number(m[1]);
    var mo = Number(m[2]);
    var d = Number(m[3]);
    var h = Number(m[4]);
    var mi = Number(m[5]);
    var s = Number(m[6] || 0);
    var ms = m[7] ? Number((m[7] + '00').slice(0, 3)) : 0;
    var day = new Date(Date.UTC(y, mo - 1, d));
    if (day.getUTCFullYear() !== y || day.getUTCMonth() !== mo - 1 || day.getUTCDate() !== d) return NaN;
    if (h > 23 || mi > 59 || s > 59) return NaN;
    var t = Date.UTC(y, mo - 1, d, h, mi, s, ms);
    if (m[8] !== 'Z') {
      var oh = Number(m[10]);
      var om = Number(m[11]);
      if (oh > 23 || om > 59) return NaN;
      t -= (m[9] === '+' ? 1 : -1) * (oh * 60 + om) * 60000;
    }
    return t;
  }

  /** True when s (epoch seconds, a JSON number) is the instant ms, to the second (floor). */
  function sameSecond(s, ms) {
    return typeof s === 'number' && isFinite(s) && s === Math.floor(ms / 1000);
  }

  function offerDef(key) {
    for (var i = 0; i < PROMO_OFFERS.length; i++) if (PROMO_OFFERS[i].key === key) return PROMO_OFFERS[i];
    return null;
  }

  /** 'tank' for the field 'tank', 'pump' for any PUMPS field, null for every other field. */
  function promoOfferKey(field) {
    for (var i = 0; i < PROMO_OFFERS.length; i++) if (has(PROMO_OFFERS[i].fields, field)) return PROMO_OFFERS[i].key;
    return null;
  }

  /**
   * Catalogue regions as customer wording, in rule order, SA and unknown
   * values left out: ['QLD', 'NSW / ACT', 'VIC'] -> 'NSW/ACT, VIC & QLD',
   * ['VIC', 'QLD'] -> 'VIC & QLD', ['VIC'] -> 'VIC', [] -> ''.
   */
  function promoRegionsText(regions) {
    var names = TANK_PROMO.regions
      .filter(function (r) {
        return has(regions, r);
      })
      .map(function (r) {
        return r.replace(/\s*\/\s*/g, '/');
      });
    if (names.length < 2) return names.join('');
    return names.slice(0, -1).join(', ') + ' & ' + names[names.length - 1];
  }

  function termsFor(regions) {
    var text = promoRegionsText(regions);
    return text ? text + ' delivery only' : '';
  }

  /** The unique strings of v that pass ok(), in v's order; null when v is not an array. */
  function listOf(v, ok) {
    if (!Array.isArray(v)) return null;
    var out = [];
    v.forEach(function (x) {
      if (typeof x === 'string' && ok(x) && out.indexOf(x) === -1) out.push(x);
    });
    return out;
  }

  /**
   * One offer of a kx_promo.offers snapshot, checked field by field (fail
   * closed). Returns { ok: true, ...normalised } or { reason } when the offer
   * must not show. Required once enabled: pct (a number, 0 < pct <= the
   * rule's), regions (those in the rule are kept; none left = off), startsAt
   * and endsAt (ISO instants with a zone, start before end) each with
   * startsAtS / endsAtS (the same instant in epoch seconds, floor), and
   * skuPrefixes (tank: the rule's SL- / ML- are kept) or variantIds (pump:
   * ProductVariant gids are kept). label and lastDay are display only: a
   * blank or bad one gives the default label / ''.
   */
  function snapshotSpec(key, o) {
    if (!isObject(o)) return { reason: 'missing' };
    if (o.enabled !== true) return { reason: 'disabled' };
    var rule = TANK_PROMO[key];
    var pct = o.pct;
    if (typeof pct !== 'number' || !isFinite(pct) || !(pct > 0) || pct > rule.pct) return { reason: 'invalid pct' };
    var regions = listOf(o.regions, promoRegionEligible);
    if (!regions || !regions.length) return { reason: 'invalid regions' };
    var startsAtMs = instantMs(o.startsAt);
    if (!isFinite(startsAtMs) || !sameSecond(o.startsAtS, startsAtMs)) return { reason: 'invalid startsAt' };
    var endsAtMs = instantMs(o.endsAt);
    if (!isFinite(endsAtMs) || !sameSecond(o.endsAtS, endsAtMs)) return { reason: 'invalid endsAt' };
    if (!(startsAtMs < endsAtMs)) return { reason: 'invalid window' };
    var spec = {
      ok: true,
      label: typeof o.label === 'string' ? o.label.trim() : '',
      pct: pct,
      regions: regions,
      startsAt: o.startsAt.trim(),
      startsAtMs: startsAtMs,
      endsAt: o.endsAt.trim(),
      endsAtMs: endsAtMs,
      lastDay: typeof o.lastDay === 'string' && isFinite(promoEndMs(o.lastDay)) ? o.lastDay.trim() : '',
    };
    if (key === 'tank') {
      spec.skuPrefixes = listOf(o.skuPrefixes, function (p) {
        return has(rule.skuPrefixes, p);
      });
      if (!spec.skuPrefixes || !spec.skuPrefixes.length) return { reason: 'invalid skuPrefixes' };
    } else {
      spec.variantIds = listOf(o.variantIds, function (id) {
        return VARIANT_GID.test(id);
      });
      if (!spec.variantIds || !spec.variantIds.length) return { reason: 'invalid variantIds' };
    }
    return spec;
  }

  /**
   * One offer of the LEGACY settings shape { enabled, label, endsAt: last day
   * "YYYY-MM-DD" (Sydney) }: what the old theme settings printed. No theme
   * prints it any more; the tests, the harness and the shared promo-cases
   * still describe "which discounts run, until which day" with it. It never
   * narrows the rule (all regions, both prefixes, any PUMPS variant).
   */
  function settingsSpec(s) {
    var o = isObject(s) ? s : {};
    var endsAt = String(o.endsAt == null ? '' : o.endsAt).trim();
    var endsAtMs = promoEndMs(endsAt);
    var spec = {
      label: String(o.label == null ? '' : o.label).trim(),
      endsAt: endsAt,
      endsAtMs: endsAtMs,
      lastDay: isFinite(endsAtMs) ? endsAt : '',
    };
    if (o.enabled !== true) spec.reason = 'disabled';
    else if (!isFinite(endsAtMs)) spec.reason = 'invalid endsAt';
    else spec.ok = true;
    return spec;
  }

  /** Legacy settings: { tank, pump }, or the oldest flat { enabled, label, endsAt } = the tank offer only. */
  function promoSettings(s) {
    var o = isObject(s) ? s : {};
    if (isObject(o.tank) || isObject(o.pump)) {
      return { tank: isObject(o.tank) ? o.tank : {}, pump: isObject(o.pump) ? o.pump : {} };
    }
    return { tank: o, pump: {} };
  }

  /** A policy's percentage can lower the rule's, never raise it. */
  function cappedPct(rulePct, policyPct) {
    if (policyPct == null) return rulePct;
    var n = Number(policyPct);
    return isFinite(n) && n > 0 ? Math.min(rulePct, n) : 0;
  }

  /** One offer at instant `at`, from a checked spec (see promoPolicy for the fields). */
  function offerState(key, spec, at) {
    var def = offerDef(key);
    var rule = TANK_PROMO[key];
    var regions = spec.regions || TANK_PROMO.regions;
    var o = {
      enabled: false,
      scheduled: false,
      status: 'off',
      reason: spec.reason || '',
      label: spec.label || def.defaultLabel,
      startsAt: spec.startsAt || '',
      startsAtMs: isFinite(spec.startsAtMs) ? spec.startsAtMs : NaN,
      endsAt: spec.endsAt || '',
      endsAtMs: isFinite(spec.endsAtMs) ? spec.endsAtMs : NaN,
      lastDay: spec.lastDay || '',
      pct: spec.pct === undefined ? rule.pct : cappedPct(rule.pct, spec.pct),
      regions: regions.slice(),
      regionsText: promoRegionsText(regions),
      terms: termsFor(regions),
    };
    if (key === 'tank') {
      o.skuPrefixes = (spec.skuPrefixes || rule.skuPrefixes).slice();
      // SL- is Slimline, ML- Modline (the catalogue's SKU naming, same order as rule.types).
      o.types = rule.types.filter(function (t, i) {
        return has(o.skuPrefixes, rule.skuPrefixes[i]);
      });
    } else {
      o.withTypes = rule.withTypes.slice();
      o.perTank = rule.perTank;
      o.fields = PUMP_FIELDS.slice();
      o.variantIds = spec.variantIds ? spec.variantIds.slice() : null;
    }
    if (spec.ok === true) {
      if (!isFinite(at)) {
        o.reason = 'invalid now';
      } else if (isFinite(o.startsAtMs) && at < o.startsAtMs) {
        o.scheduled = true;
        o.status = 'scheduled';
        o.reason = 'not started';
      } else if (!(at < o.endsAtMs)) {
        o.status = 'ended';
        o.reason = 'ended';
      } else {
        o.enabled = true;
        o.status = 'live';
      }
    }
    return o;
  }

  /**
   * What promoPolicy() reads when no input is passed: window.KingspanTankOffers,
   * which layout/theme.liquid always defines (the metafield's value, or null
   * when there is none: no offer). No theme setting is ever read (user decision
   * 3: no fallback): a theme that prints the old window.KingspanTankPromoSettings
   * instead (an unported layout) shows no offer. Only the test pages and the
   * harness, which have no theme.liquid, may describe offers with the legacy
   * settings shape, under window.KingspanTankPromoSettingsForTests.
   */
  function defaultOffersInput() {
    if (typeof window === 'undefined' || !window) return null;
    if (hasOwn(window, 'KingspanTankOffers')) return window.KingspanTankOffers;
    return window.KingspanTankPromoSettingsForTests || null;
  }

  /*
   * The page's clock for the offers: this device's clock, moved forward when it
   * is behind the time the page was rendered (window.KingspanServerNowS, epoch
   * seconds, printed by layout/theme.liquid). A cached page only makes that
   * render time older, so this never shows an offer earlier or for longer than
   * the device clock alone would; it stops a slow device clock keeping an offer
   * up after checkout has stopped applying it. Measured once, when this loads.
   */
  var CLOCK_SKEW_MS = (function () {
    var s = typeof window !== 'undefined' && window ? window.KingspanServerNowS : undefined;
    if (typeof s !== 'number' || !isFinite(s) || s <= 0) return 0;
    return Math.max(0, s * 1000 - Date.now());
  })();

  function nowMs() {
    return Date.now() + CLOCK_SKEW_MS;
  }

  function regionsWhere(policy, keys, all) {
    var out = null;
    keys.forEach(function (k) {
      var o = policy && policy[k];
      var r = o && Array.isArray(o.regions) ? o.regions : [];
      if (out === null) out = r.slice();
      else
        out = all
          ? out.filter(function (x) {
              return has(r, x);
            })
          : out.concat(r);
    });
    return out || [];
  }

  function liveOfferKeys(policy) {
    return PROMO_OFFERS.map(function (d) {
      return d.key;
    }).filter(function (k) {
      return !!(policy && policy[k] && policy[k].enabled === true);
    });
  }

  /**
   * Both offers as they stand at `now` (default: the page's clock, i.e.
   * Date.now() moved forward to the render time when the device clock is
   * behind; also a Date, epoch ms or an ISO string). `input` is, by shape:
   *  - a kx_promo.offers snapshot (has a "v" key): exactly what checkout runs.
   *    v must be 1; then each offer is checked by snapshotSpec (fail closed);
   *  - legacy settings { tank: { enabled, label, endsAt }, pump: {...} } (or
   *    the oldest flat { enabled, label, endsAt } = tank only): tests only;
   *  - omitted / null: window.KingspanTankOffers (see defaultOffersInput).
   * Each offer fails closed on its own. Returns
   *   { source: 'snapshot' | 'settings' | 'none', updatedAt,
   *     enabled,            // true when either offer is live now
   *     tank: OFFER, pump: OFFER,
   *     regions,            // the rule's regions (narrowing for hand-made policies)
   *     regionsText, terms, // where any live offer applies: 'NSW/ACT, VIC & QLD'
   *                         // + ' delivery only' ('' when none is live)
   *     label }             // deprecated: tank.label; use promoLabels()
   * OFFER = { enabled (live now), scheduled (valid, waiting for its start),
   *   status: 'live' | 'scheduled' | 'ended' | 'off', reason ('' when live,
   *   else why not: 'missing', 'unknown version', 'disabled', 'invalid pct',
   *   'invalid regions', 'invalid startsAt', 'invalid endsAt', 'invalid
   *   window', 'invalid skuPrefixes', 'invalid variantIds', 'not started',
   *   'ended', 'invalid now'), label (blank -> the default label), startsAt,
   *   startsAtMs, endsAt, endsAtMs, lastDay, pct, regions, regionsText, terms,
   *   tank: types, skuPrefixes | pump: withTypes, perTank, fields,
   *   variantIds (null = any PUMPS variant; legacy settings) }.
   */
  function promoPolicy(input, now) {
    var src = input == null ? defaultOffersInput() : input;
    var at = now == null ? nowMs() : typeof now === 'string' ? Date.parse(now) : Number(now);
    var source = 'none';
    var updatedAt = '';
    var specs = { tank: { reason: 'missing' }, pump: { reason: 'missing' } };
    if (isObject(src) && hasOwn(src, 'v')) {
      source = 'snapshot';
      if (src.v === SNAPSHOT_VERSION) {
        specs = { tank: snapshotSpec('tank', src.tank), pump: snapshotSpec('pump', src.pump) };
        updatedAt = typeof src.updatedAt === 'string' ? src.updatedAt : '';
      } else {
        specs = { tank: { reason: 'unknown version' }, pump: { reason: 'unknown version' } };
      }
    } else if (isObject(src)) {
      source = 'settings';
      var s = promoSettings(src);
      specs = { tank: settingsSpec(s.tank), pump: settingsSpec(s.pump) };
    }
    var tank = offerState('tank', specs.tank, at);
    var pump = offerState('pump', specs.pump, at);
    var policy = {
      source: source,
      updatedAt: updatedAt,
      enabled: tank.enabled || pump.enabled,
      tank: tank,
      pump: pump,
      regions: TANK_PROMO.regions,
      regionsText: '',
      terms: '',
      label: tank.label,
    };
    var live = regionsWhere(policy, liveOfferKeys(policy), false);
    policy.regionsText = promoRegionsText(live);
    policy.terms = termsFor(live);
    return policy;
  }

  /**
   * The variant gid for a pump: variant is the gid itself, or the sku map
   * (map.addons[field].variantId), but only while that entry's sku is still the
   * pump's own (PUMPS[i].sku): checkout keys each pump's floor by the line's
   * SKU, so a pump whose map entry has another SKU gets nothing off there, and
   * none here either.
   */
  function variantOf(variant, field) {
    if (typeof variant === 'string') return variant;
    var entry = addonEntry(variant, field);
    var p = pumpInfo(field);
    return entry && typeof entry.variantId === 'string' && p && entry.sku === p.sku ? entry.variantId : '';
  }

  /**
   * Percent off one priced part of a build. field: 'tank' (tank offer), a
   * PUMPS field (pump offer) or any other add-on field (always 0). typeKey:
   * the build's tank type. variant (pump fields only): the add-on's variant
   * gid, or the sku map to look it up in. Each part is gated by its own offer
   * only. The offer can narrow the regions, types, percentage and pump
   * variants, never widen them; SA is 0. A snapshot pump offer names its
   * variants, so a pump without a variant (none passed, or not in the map) is
   * 0 there (fail closed). Ignores price: see promoPriceEligible.
   */
  function promoPct(policy, typeKey, region, field, variant) {
    var key = promoOfferKey(field);
    var offer = key && policy ? policy[key] : null;
    if (!offer || offer.enabled !== true) return 0;
    if (!promoRegionEligible(region)) return 0;
    var r = String(region);
    if (policy.regions && !has(policy.regions, r)) return 0;
    if (offer.regions && !has(offer.regions, r)) return 0;
    var rule = TANK_PROMO[key];
    var types = key === 'tank' ? 'types' : 'withTypes';
    if (!has(rule[types], typeKey)) return 0;
    if (offer[types] && !has(offer[types], typeKey)) return 0;
    if (key === 'pump' && offer.variantIds != null && !has(offer.variantIds, variantOf(variant, field))) return 0;
    return cappedPct(rule.pct, offer.pct);
  }

  /** Cents off one line: Shopify rounds a percentage on the line subtotal (unit x qty). */
  function lineDiscountCents(unitPrice, qty, pct) {
    var cents = Math.round(Number(unitPrice) * 100);
    if (!pct || !(cents > 0)) return 0;
    return Math.round((cents * qty * pct) / 100);
  }

  /**
   * False only for a pump priced below its own list price
   * (PUMPS[i].minUnitPrice): nothing comes off a pump that is already
   * reduced. True for the tank (no floor) and every other field.
   */
  function promoPriceEligible(field, unitPrice) {
    if (promoOfferKey(field) !== 'pump') return true;
    var p = pumpInfo(field);
    return !!p && Number(unitPrice) >= p.minUnitPrice;
  }

  /**
   * What the offers take off a build, in dollars, for qty of it (line
   * subtotals, rounded to the cent like Shopify; qty defaults to 1). The tank
   * is gated by the tank offer; pumps by the pump offer, each at or above its
   * own list price, and only ONE pump per tank: the first in PUMPS order that
   * qualifies (a build should hold one pump; checkout ranks pump lines in the
   * same order if one somehow holds two).
   * parts: { typeKey, region, tankPrice, addons: { field: unitPrice }, qty,
   *          map: the sku map (tank-sku-map.json; each pump's variant gid),
   *          addonVariants: { field: variant gid } (overrides map) }
   * Pass map (or addonVariants): a snapshot pump offer names its variants,
   * and without them no pump is discounted (fail closed).
   * Returns { tank, addons: { field: amount } (discounted fields only), total }.
   */
  function promoBreakdown(policy, parts) {
    var p = parts || {};
    var qty = Math.max(1, Math.floor(Number(p.qty) || 1));
    var tankCents = lineDiscountCents(p.tankPrice, qty, promoPct(policy, p.typeKey, p.region, 'tank'));
    var out = { tank: tankCents / 100, addons: {}, total: 0 };
    var totalCents = tankCents;
    var addons = isObject(p.addons) ? p.addons : {};
    var variants = isObject(p.addonVariants) ? p.addonVariants : null;
    var pumpsLeft = TANK_PROMO.pump.perTank;
    PUMP_FIELDS.forEach(function (field) {
      if (!hasOwn(addons, field) || pumpsLeft <= 0) return;
      var unit = addons[field];
      var variant = variants && hasOwn(variants, field) ? variants[field] : p.map;
      var pct = promoPct(policy, p.typeKey, p.region, field, variant);
      if (!(pct > 0) || !promoPriceEligible(field, unit)) return;
      pumpsLeft -= 1; // this pump is the tank's one discounted pump, as at checkout
      var c = lineDiscountCents(unit, qty, pct);
      if (c) {
        out.addons[field] = c / 100;
        totalCents += c;
      }
    });
    out.total = totalCents / 100;
    return out;
  }

  function offerLabel(policy, def) {
    var state = (policy && policy[def.key]) || {};
    return String(state.label == null ? '' : state.label).trim() || def.defaultLabel;
  }

  /** An offer's own "<states> delivery only" (the rule's wording for a policy without regions). */
  function offerTerms(policy, key) {
    var o = policy && policy[key];
    return termsFor(o && Array.isArray(o.regions) ? o.regions : TANK_PROMO.regions);
  }

  /**
   * One entry per discounted part of a promoBreakdown() result, tank first,
   * then the pump (PUMPS order), for the offer lines, rows and chips:
   *   { offer: 'tank'|'pump', field: 'tank' | the pump's field,
   *     label,   // that offer's label (the discount's message)
   *     item,    // 'tank' | PUMPS[i].item, e.g. 'Claytech C3X pump'
   *     amount,  // dollars off, as in the breakdown (for the qty it was given)
   *     terms,   // that offer's regions: 'NSW/ACT, VIC & QLD delivery only'
   *     text }   // label + ' — ' + item + ' (' + terms + ')'
   * [] when nothing is discounted. The two offers can name different
   * regions: for one line under a total use promoTerms(policy, breakdown).
   */
  function promoLabels(policy, breakdown) {
    var b = breakdown || {};
    var out = [];
    PROMO_OFFERS.forEach(function (def) {
      def.fields.forEach(function (field) {
        var amount = Number(field === 'tank' ? b.tank : b.addons && b.addons[field]) || 0;
        if (!(amount > 0)) return;
        var label = offerLabel(policy, def);
        var item = field === 'tank' ? 'tank' : pumpInfo(field).item;
        var terms = offerTerms(policy, def.key);
        out.push({
          offer: def.key,
          field: field,
          label: label,
          item: item,
          amount: amount,
          terms: terms,
          text: label + ' — ' + item + ' (' + terms + ')',
        });
      });
    });
    return out;
  }

  /**
   * The "<states> delivery only" wording, from the offers' own regions:
   *  - which 'tank' | 'pump': that offer's, while it is live;
   *  - which = a promoBreakdown() result: where ALL of that saving applies
   *    (the regions shared by every offer that took something off), for
   *    "You save $X · VIC delivery only";
   *  - which omitted: where any live offer applies.
   * '' when that leaves no region (offer not live, nothing taken off).
   */
  function promoTerms(policy, which) {
    if (which === 'tank' || which === 'pump') {
      return policy && policy[which] && policy[which].enabled === true ? offerTerms(policy, which) : '';
    }
    if (isObject(which)) {
      var keys = [];
      if (Number(which.tank) > 0) keys.push('tank');
      var addons = isObject(which.addons) ? which.addons : {};
      if (
        PUMP_FIELDS.some(function (f) {
          return Number(addons[f]) > 0;
        })
      ) {
        keys.push('pump');
      }
      return keys.length ? termsFor(regionsWhere(policy, keys, true)) : '';
    }
    return termsFor(regionsWhere(policy, liveOfferKeys(policy), false));
  }

  /**
   * The note for one part of a build while its offer applies to it, e.g.
   * '10% off with your tank — NSW/ACT, VIC & QLD delivery only' under a pump
   * chip; '' when promoPct() is 0, or when unitPrice is given and
   * promoBreakdown() would take nothing off it (a pump below its list price).
   * variant: as promoPct (the variant gid or the sku map).
   */
  function promoHint(policy, typeKey, region, field, unitPrice, variant) {
    if (!(promoPct(policy, typeKey, region, field, variant) > 0)) return '';
    if (unitPrice !== undefined && !promoPriceEligible(field, unitPrice)) return '';
    var key = promoOfferKey(field);
    return offerLabel(policy, offerDef(key)) + ' — ' + offerTerms(policy, key);
  }

  /**
   * One unit of a part after its offer, in dollars: the unit price less what
   * promoBreakdown() takes off one of it (the MKIV at 1250 -> 1125 while the
   * pump offer applies), or the unit price itself when nothing comes off (no
   * offer, SA, below the pump's list price, a variant the offer does not
   * name). NaN for a price that is not a number. For "$1,125.00 with your
   * tank" on a pump chip; savings belong in the total. variant: as promoPct.
   */
  function promoNetPrice(policy, typeKey, region, field, unitPrice, variant) {
    var unit = Number(unitPrice);
    if (unitPrice == null || unitPrice === '' || !isFinite(unit)) return NaN;
    var pct = promoPct(policy, typeKey, region, field, variant);
    if (!(pct > 0) || !promoPriceEligible(field, unit)) return unit;
    return (Math.round(unit * 100) - lineDiscountCents(unit, 1, pct)) / 100;
  }

  /**
   * When the next live offer ends or the next scheduled one starts (epoch
   * ms), or NaN when neither: an open page re-prices then, so it never shows
   * an offer checkout has stopped, nor misses one checkout has started.
   */
  function promoNextChangeMs(policy) {
    var next = NaN;
    PROMO_OFFERS.forEach(function (def) {
      var o = policy && policy[def.key];
      var at = !o ? NaN : o.enabled === true ? o.endsAtMs : o.scheduled === true ? o.startsAtMs : NaN;
      if (typeof at === 'number' && isFinite(at) && !(at >= next)) next = at;
    });
    return next;
  }

  /**
   * Calls fn once, just after the next change (promoNextChangeMs; setTimeout
   * can't wait more than ~24.8 days, so a later one re-arms). Returns a
   * cancel function. Callers re-arm from fn with a fresh promoPolicy().
   */
  function onPromoChange(policy, fn) {
    var MAX_WAIT = 2147483647;
    var at = promoNextChangeMs(policy);
    var timer = null;
    if (!isFinite(at) || typeof setTimeout !== 'function') return function () {};
    (function arm() {
      var wait = at - nowMs();
      timer = wait > MAX_WAIT ? setTimeout(arm, MAX_WAIT) : setTimeout(fn, Math.max(0, wait) + 1000);
    })();
    return function () {
      if (typeof clearTimeout === 'function') clearTimeout(timer);
    };
  }

  /*
   * Offer API for the builders, the chat and the cards (tester round 2):
   *   var policy = promoPolicy();                    // reads window.KingspanTankOffers
   *   var b = promoBreakdown(policy, { typeKey, region, tankPrice,
   *             addons: { field: unitPrice }, qty, map: skuMap });  // map: REQUIRED for pumps
   *   b.total                                        // "You save $X" (per qty given)
   *   promoTerms(policy, b)                          // "· VIC delivery only" ('' when b.total is 0)
   *   promoLabels(policy, b)                         // breakdown rows: one per offer that took something off
   *   promoNetPrice(policy, typeKey, region, field, unitPrice, skuMap)  // "$1,125.00 with your tank"
   *   promoHint(policy, typeKey, region, field, unitPrice, skuMap)      // the note under a chip
   *   onPromoChange(policy, rerender)                // re-arm with a fresh promoPolicy() in rerender
   * PUMPS / PUMP_FIELDS / pumpInfo(field): the six pumps (order, SKU, item,
   * title, list-price floor). A pump below its list price, a variant the
   * offer does not name, SA, or a missing / malformed / disabled / not yet
   * started / ended offer: nothing off, no hint, net = unit price.
   */
  /*
   * THE cart nesting switch (user decision 2): on, the PDP builder and the chat
   * send each add-on line with parent_id = the tank's variant, so Shopify nests
   * it under the tank line; off (the default), the lines are flat and the cart
   * still renders each build together by _kut_group. One switch for every
   * surface: turn enabled to true here, and only after the staging nesting
   * probe passes. Tests and the harness flip it at run time.
   */
  var CART_NESTING = Object.seal({ enabled: false });

  window.KingspanTankCore = {
    CART_NESTING: CART_NESTING,
    skuKey: skuKey,
    resolveTank: resolveTank,
    useBaseVariantCart: useBaseVariantCart,
    computeCapacity: computeCapacity,
    computeWeight: computeWeight,
    sizeOptions: sizeOptions,
    sizesNearCapacity: sizesNearCapacity,
    colourPalette: colourPalette,
    findColour: findColour,
    addonEntry: addonEntry,
    addonPrice: addonPrice,
    paintedTier1: paintedTier1,
    paintSurcharge: paintSurcharge,
    PAINT_TIERS: PAINT_TIERS,
    PAINT_LIMITS: PAINT_LIMITS,
    paintable: paintable,
    paintTierKey: paintTierKey,
    paintedTier: paintedTier,
    paintedColourPrice: paintedColourPrice,
    TANK_PROMO: TANK_PROMO,
    PUMPS: PUMPS,
    PUMP_FIELDS: PUMP_FIELDS,
    pumpInfo: pumpInfo,
    SNAPSHOT_VERSION: SNAPSHOT_VERSION,
    promoRegionEligible: promoRegionEligible,
    promoProvinceEligible: promoProvinceEligible,
    promoEndMs: promoEndMs,
    promoPolicy: promoPolicy,
    promoPct: promoPct,
    promoPriceEligible: promoPriceEligible,
    promoBreakdown: promoBreakdown,
    PROMO_OFFERS: PROMO_OFFERS,
    promoOfferKey: promoOfferKey,
    promoLabels: promoLabels,
    promoRegionsText: promoRegionsText,
    promoTerms: promoTerms,
    promoHint: promoHint,
    promoNetPrice: promoNetPrice,
    promoNextChangeMs: promoNextChangeMs,
    onPromoChange: onPromoChange,
  };
})();
